import React, { useLayoutEffect, useRef, useState } from "react";
import { Stack, TextField, type ITextFieldStyles } from "@fluentui/react";
import { SendRegular } from "@fluentui/react-icons";
import styles from "./QuestionInput.module.css";

const questionComposerTextFieldStyles: Partial<ITextFieldStyles> = {
  root: { width: "100%" },
  fieldGroup: { border: "none", background: "transparent" },
  field: { fontSize: 15, lineHeight: 1.45, minHeight: 22, maxHeight: 300 },
};

export type QuestionInputProps = {
  onSend: (question: string) => void;
  disabled?: boolean;
  placeholder?: string;
  clearOnSend?: boolean;
  isGenerating?: boolean;
};

export function QuestionInput({
  onSend,
  disabled = false,
  placeholder = "Ask the knowledge base...",
  clearOnSend = true,
  isGenerating = false,
}: QuestionInputProps) {
  const [question, setQuestion] = useState("");
  const textFieldMountRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const area = textFieldMountRef.current?.querySelector("textarea");
    if (!area) {
      return;
    }
    area.style.height = "auto";
    const next = Math.min(300, Math.max(22, area.scrollHeight));
    area.style.height = `${next}px`;
  }, [question]);

  const send = () => {
    if (disabled || isGenerating) {
      onSend(question);
      return;
    }
    onSend(question);
    if (clearOnSend) {
      setQuestion("");
    }
  };

  const canSend = !disabled && !isGenerating;

  return (
    <div className={styles.questionInputContainer}>
      <div className={styles.inputRowContainer}>
        <div ref={textFieldMountRef} className={`cordexa-composer-shell ${styles.textInputContainer}`}>
          <button
            type="button"
            className={styles.questionInputFileUpload}
            disabled
            title="Attachments are not part of this knowledge base"
            aria-label="Attachments are not part of this knowledge base"
          >
            <svg
              className={styles.fileUploadIcon}
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
          </button>
          <TextField
            styles={questionComposerTextFieldStyles}
            className={styles.questionInputTextArea}
            placeholder={placeholder}
            data-testid="console-ask-input"
            multiline
            rows={1}
            resizable={false}
            borderless
            value={question}
            onChange={(_, value) => setQuestion(value ?? "")}
            onKeyDown={(event) => {
              if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault();
                send();
              }
            }}
          />
        </div>
        <Stack className={styles.microphoneAndSendContainer} horizontal>
          <button
            type="button"
            className={
              canSend ? styles.questionInputSendButton : styles.questionInputSendButtonDisabled
            }
            onClick={send}
            disabled={!canSend}
            aria-label="Send"
            data-testid="console-ask-submit"
          >
            <SendRegular />
          </button>
        </Stack>
      </div>
    </div>
  );
}

export default QuestionInput;
